import React, { useState, useEffect } from "react";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import axios from "axios";
import {
    Table, Button, Modal, ModalHeader, ModalBody, ModalFooter,
    Container, Input, FormGroup, Label
} from "reactstrap";
import { Link } from "react-router-dom";
import { useUser } from './Context/UserContext';
import profileImg from "../assets/logo.png";
import "../styles/ViewAllInvoiceDetails.css";
import "../styles/Reports.css";
import DashboardLayout from "../../src/components/DashboardLayout";
import VendorInfoTabs from "./VendorInfoTabs";

const ViewAllInvoiceDetails = () => {
    const [showReminderToast, setShowReminderToast] = useState(false);
    const [reminderInvoice, setReminderInvoice] = useState(null);
    const [sidebarOpen, setSidebarOpen] = useState(true);
    const [showCard, setShowCard] = useState(false);
    const toggleSidebar = () => setSidebarOpen(!sidebarOpen);
    const { user } = useUser();
    const [errors, setErrors] = useState({});
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [invoiceDetails, setInvoiceDetails] = useState([]);
    const [invoiceInfo, setInvoiceInfo] = useState(null);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [approvedStatusList, setApprovedStatusList] = useState([]);
    const [selectedVendor, setSelectedVendor] = useState(null);
    const [vendorFiles, setVendorFiles] = useState({});
    const [subTab, setSubTab] = useState("details");
    const [isEditing, setIsEditing] = useState(false);
    const [showPreview, setShowPreview] = useState(false);
    const [previewUrl, setPreviewUrl] = useState("");
    const [previewTitle, setPreviewTitle] = useState("");
    const BASE_URL = "/";
    const [sortBy, setSortBy] = useState("");
    const [sortOrder, setSortOrder] = useState("asc");
    const [dateError, setDateError] = useState("");
    const [showFilters, setShowFilters] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [vendorDetails, setVendorDetails] = useState([]);
    const [departmentHeads, setDepartmentHeads] = useState([]);
    const [deptId, setDeptId] = useState("");
    const userRole = user?.roles?.[0]?.role?.trim().toLowerCase() || "";
    const isFinanceTeam = userRole.includes("finance");
    // ---- Invoice visibility: Finance/CFO/Super Admin see all, others see only their own ----
    const seeAllInvoices = (user?.roles || []).some(r => {
        const name = (r?.role || "").trim().toLowerCase();
        return name.includes("finance") || name.includes("cfo") || name.includes("super admin");
    });
    const userEmpId = seeAllInvoices ? "" : String(user?.empId || "");

    const REPORT_URL = "/api/ViewAllInvoiceDetails/GetInvoiceReports";

    const fetchReports = async (baseParams = {}) => {
        if (!userEmpId) {
            const res = await axios.get(REPORT_URL, { params: baseParams });
            return res.data;
        }
        const [asDeptHead, asVerifier] = await Promise.all([
            axios.get(REPORT_URL, { params: { ...baseParams, deptId: userEmpId, verifierId: "" } }),
            axios.get(REPORT_URL, { params: { ...baseParams, verifierId: userEmpId, deptId: "" } })
        ]);
        const map = new Map();
        [...asDeptHead.data, ...asVerifier.data].forEach(inv => {
            const key = inv.id ?? inv.ID;
            if (!map.has(key)) map.set(key, inv);
        });
        return Array.from(map.values());
    };
    // ✅ Modal state (new)
    const [showModal, setShowModal] = useState(false);
    const [selectedInvoice, setSelectedInvoice] = useState(null);
    const [activeTab, setActiveTab] = useState("history");
    const [approvalHistory, setApprovalHistory] = useState([]);
    const [agreementRefs, setAgreementRefs] = useState([]);

    const [formData, setFormData] = useState({
        invoiceType: 0,
        supplierType: 0,
        approvedStatus: 0,
        invoiceNo: "",
        deliveryChallanNumber: ""
    });

    const handleInputChange = (e) => {
        const { id, value } = e.target;
        setFormData((prev) => ({ ...prev, [id]: value }));
    };

    useEffect(() => {
        const fetchDepartmentHeads = async () => {
            try {
                const res = await axios.get("/department/department-heads");
                setDepartmentHeads(res.data);
            } catch (err) {
                console.error("Failed to load department heads", err);
            }
        };
        fetchDepartmentHeads();
    }, []);

    useEffect(() => {
        const dark = localStorage.getItem("darkMode") === "true";
        document.body.classList.toggle("dark-mode", dark);
    }, []);


    // ✅ Handle eye button click
    const handleView = (invoice) => {
        setSelectedInvoice(invoice);
        setActiveTab("history");
        setShowModal(true);
    };
    //
    const handleReminder = async (invoice) => {
        try {
            const recordId = invoice.ID || invoice.id;

            if (!recordId) {
                console.error("No invoice ID found on this row:", invoice);
                return;
            }

            const res = await axios.post("/api/approvalstatus/send-reminder", {
                id: recordId
            });

            console.log("Reminder email sent successfully");
            setReminderInvoice({ ...invoice, testEmail: res.data.testEmail });
            setShowReminderToast(true);
            setTimeout(() => setShowReminderToast(false), 5000);

        } catch (err) {
            const msg = err.response?.data?.message || err.message;

            // ✅ show toast even on failure so user sees something
            if (msg.includes("ApprovalStatus may not match") || msg.includes("No recipients found")) {
                setReminderInvoice({
                    ...invoice,
                    testEmail: "N/A — Invoice status not eligible for reminder"
                });
                setShowReminderToast(true);
                setTimeout(() => setShowReminderToast(false), 5000);
            }

            console.error("Failed to send reminder email:", msg);
        }
    };

    const handleSearch = async (e) => {
        e.preventDefault();
        const params = {
            invoiceType: Number(formData.invoiceType) || 0,
            supplierType: Number(formData.supplierType) || 0,
            approvedStatus: Number(formData.approvedStatus) || 0,
            invoiceNumber: formData.invoiceNo?.trim() || "",
            deliveryChallanNumber: formData.deliveryChallanNumber?.trim() || "",
            createdDate: "",
            departmentHead: "",
            verifierId: "",
            deptId: deptId?.trim() || "",
            search: ""
        };
        try {
            const data = await fetchReports(params);
            setInvoiceDetails(data);
            setCurrentPage(1);
        } catch (error) {
            console.error("Search failed:", error.response?.data || error.message);
            setInvoiceDetails([]);
        }
    };

    const handleSort = (column) => {
        const sortedInvoiceDetails = [...invoiceDetails].sort((a, b) => {
            let valueA = "";
            let valueB = "";
            if (column === "approvalStatus") {
                valueA = a.approvalStatusTitle?.toLowerCase() || "";
                valueB = b.approvalStatusTitle?.toLowerCase() || "";
            }
            if (sortOrder === "asc") {
                return valueA > valueB ? 1 : valueA < valueB ? -1 : 0;
            } else {
                return valueA < valueB ? 1 : valueA > valueB ? -1 : 0;
            }
        });
        setInvoiceDetails(sortedInvoiceDetails);
        setSortOrder(sortOrder === "asc" ? "desc" : "asc");
        setSortBy(column);
    };

    useEffect(() => {
        if (!user?.empId) return;
        const load = async () => {
            try {
                const data = await fetchReports();
                setInvoiceDetails(data);
                setTotalPages(Math.ceil(data.length / itemsPerPage));
                setCurrentPage(1);
            } catch (error) {
                console.error("Error fetching initial vendor data:", error);
                setInvoiceDetails([]);
                setTotalPages(1);
                setCurrentPage(1);
            }
        };
        load();
    }, [user?.empId]);

    useEffect(() => {
        const fetchApprovedStatuses = async () => {
            try {
                const response = await axios.get("/api/ViewAllInvoiceDetails/GetApprovedStatusList");
                setApprovedStatusList(response.data);
            } catch (error) {
                console.error("Error fetching approved status list:", error);
            }
        };
        fetchApprovedStatuses();
    }, []);
    useEffect(() => {
        if (!selectedInvoice) return;

        // ✅ Backend DTO maps to "ID" (uppercase) from GetInvoiceReports
        const recordId = selectedInvoice?.ID || selectedInvoice?.id;
        console.log("Invoice ID for history:", recordId, "| Full object:", selectedInvoice);

        if (!recordId) {
            setApprovalHistory([]);
            return;
        }

        axios
            .get(`/api/approvalstatus/invoicestatus/${recordId}`)
            .then(res => {
                setApprovalHistory(res.data || []);
            })
            .catch(err => {
                console.error("Invoice history fetch failed:", err.response?.data || err.message);
                setApprovalHistory([]);
            });
    }, [selectedInvoice]);
    useEffect(() => {
        if (!selectedInvoice) return;
        const recordId = selectedInvoice?.ID || selectedInvoice?.id;
        if (!recordId) {
            setInvoiceInfo(null);
            return;
        }
        axios
            .get(`/api/invoices/info/${recordId}`)
            .then(res => setInvoiceInfo(res.data))
            .catch(err => {
                console.error("Invoice info fetch failed:", err.response?.data || err.message);
                setInvoiceInfo(null);
            });
    }, [selectedInvoice]);

    useEffect(() => {
        if (!invoiceInfo?.userid) {
            setSelectedVendor(null);
            setVendorFiles({});
            return;
        }
        axios
            .get(`/api/vendordetails/vendor-by-userid/${invoiceInfo.userid}`)
            .then((res) => {
                const vendorData = { ...res.data, id: res.data.vendorId };
                setSelectedVendor(vendorData);
                return axios.get(`/api/vendor/files/vendor/${vendorData.vendorId}`);
            })
            .then((filesRes) => {
                const filesMapped = {};
                filesRes.data.forEach((file) => {
                    filesMapped[file.docName.toUpperCase()] = {
                        fileId: file.fileId,
                        fileName: file.fileName,
                        fileExtension: file.fileExtension,
                        filePath: `/api/vendor/files/${file.fileId}?mode=view`,
                    };
                });
                setVendorFiles(filesMapped);
            })
            .catch((err) => {
                console.error("Vendor fetch failed:", err);
                setSelectedVendor(null);
                setVendorFiles({});
            });
    }, [invoiceInfo]);

    useEffect(() => {
        const vid = selectedVendor?.vendorId;
        if (!vid) {
            setAgreementRefs([]);
            return;
        }
        axios
            .get(`/api/vendoragreement/by-vendor/${vid}`)
            .then((res) => setAgreementRefs(Array.isArray(res.data) ? res.data : []))
            .catch((err) => {
                console.error("Error fetching agreement references:", err);
                setAgreementRefs([]);
            });
    }, [selectedVendor?.vendorId]);

    const handleClear = async () => {
        setFormData({
            invoiceType: "",
            supplierType: "",
            invoiceNo: "",
            deliveryChallanNumber: "",
            approvedStatus: "",
        });
        setDeptId("");
        setErrors({});
        try {
            const data = await fetchReports();
            setInvoiceDetails(data);
            setCurrentPage(1);
        } catch (error) {
            console.error("Error reloading vendor data:", error);
            setInvoiceDetails([]);
            setTotalPages(1);
            setCurrentPage(1);
        }
    };

    const handleRowsChange = (e) => {
        const value = e.target.value;
        if (!value) { setRowsPerPage(10); setCurrentPage(1); return; }
        const parsed = parseInt(value);
        if (!isNaN(parsed)) { setRowsPerPage(parsed); setCurrentPage(1); }
    };

    const handleDownload = async (fileUrl, downloadName) => {
        try {
            if (fileUrl.startsWith("data:")) {
                const a = document.createElement("a");
                a.href = fileUrl;
                a.download = downloadName;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                return;
            }

            const res = await axios.get(fileUrl, { responseType: "blob" });
            const blobUrl = window.URL.createObjectURL(res.data);

            const a = document.createElement("a");
            a.href = blobUrl;
            a.download = downloadName;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.URL.revokeObjectURL(blobUrl);
        } catch (err) {
            console.error("Download failed:", err);
            alert("Unable to download file");
        }
    };

    const renderDocumentFile = (fileData, fileName, title) => {
        if (!fileData || typeof fileData !== "string" || fileData.trim() === "") {
            return <span style={{ color: "gray" }}>No file uploaded</span>;
        }
        let fileUrl = "";
        let cleanPath = fileData.trim();
        cleanPath = cleanPath.replace(/\\/g, "/");
        cleanPath = cleanPath.replace(/^~\/?/, "");
        cleanPath = cleanPath.replace(/^invoicecopy/i, "InvoiceCopy");

        if (cleanPath.startsWith("http")) {
            fileUrl = cleanPath;
        } else if (cleanPath.startsWith("JVBER")) {
            fileUrl = `data:application/pdf;base64,${cleanPath}`;
        } else {
            fileUrl = `${BASE_URL.replace(/\/$/, "")}/${cleanPath.replace(/^\/+/, "")}`;
        }

        const displayName =
            fileName ||
            (cleanPath.includes("/") ? cleanPath.split("/").pop() : "Document");

        return (
            <div style={{ display: "flex", flexDirection: "column" }}>
                <span style={{ color: "#0a4275", fontWeight: "500" }}>{displayName}</span>
                <div style={{ display: "flex", gap: "8px", marginTop: "5px" }}>
                    <Button
                        color="primary"
                        size="sm"
                        outline
                        onClick={() => window.open(fileUrl, "_blank")}
                    >
                        View
                    </Button>
                </div>
            </div>
        );
    };
    const timesheetName = () => invoiceInfo?.fileNameTimeSheet || "timesheet";

    const isMsgTimesheet = () =>
        /\.msg$/i.test(invoiceInfo?.fileNameTimeSheet || "") ||
        /\.msg$/i.test(invoiceInfo?.timeSheetFilePath || "");

    const isViewableTimesheet = () =>
        /\.(pdf|png|jpe?g|gif|webp)$/i.test(timesheetName());
    const isExcelTimesheet = () =>
        /\.(xlsx?|xlsm|csv)$/i.test(timesheetName());

    // PDF / image: open in a new tab
    const viewTimesheet = () => {
        const path = (invoiceInfo?.timeSheetFilePath || "")
            .replace(/\\/g, "/").replace(/^~\/?/, "").replace(/^\/+/, "");
        window.open(`${window.location.origin}/${path}`, "_blank");
    };

    // Any type: download through the backend with the original name
    const downloadTimesheet = async () => {
        const invoiceId = selectedInvoice?.ID || selectedInvoice?.id;
        try {
            const res = await axios.get(
                `/api/invoices/timesheet-download/${invoiceId}`,
                { responseType: "blob" }
            );
            const blobUrl = window.URL.createObjectURL(
                new Blob([res.data], { type: "application/octet-stream" })
            );
            const a = document.createElement("a");
            a.href = blobUrl;
            a.download = timesheetName();
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.URL.revokeObjectURL(blobUrl);
        } catch (err) {
            console.error("Timesheet download failed:", err);
            alert("Unable to download the timesheet.");
        }
    };

    // msg: show the message content
    const viewTimesheetMessage = async () => {
        const invoiceId = selectedInvoice?.ID || selectedInvoice?.id;
        try {
            const res = await axios.get(`/api/invoices/timesheet-message/${invoiceId}`);
            alert(
                `Subject: ${res.data.subject}\n\n` +
                `From: ${res.data.from}\n\n` +
                `Message:\n${res.data.bodyText}`
            );
        } catch (err) {
            console.error("Error reading timesheet message:", err);
            alert("Unable to read the timesheet message.");
        }
    };
    const itemsPerPage = 10;

    const filteredInvoices = invoiceDetails.filter((invoice) =>
        invoice.nameOfCompany?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    useEffect(() => {
        setTotalPages(Math.max(1, Math.ceil(filteredInvoices.length / rowsPerPage)));
    }, [filteredInvoices.length, rowsPerPage]);

    const paginatedInvoices = filteredInvoices.slice(
        (currentPage - 1) * rowsPerPage,
        currentPage * rowsPerPage
    );

    const handlePageChange = (type) => {
        if (type === "first") setCurrentPage(1);
        if (type === "prev" && currentPage > 1) setCurrentPage(currentPage - 1);
        if (type === "next" && currentPage < totalPages) setCurrentPage(currentPage + 1);
        if (type === "last") setCurrentPage(totalPages);
    };

    const handleExport = async () => {
        try {
            const response = await axios.get("/api/ViewAllInvoiceDetails/GetInvoiceApprovalStagesReport");
            const invoiceDetails = response.data;
            if (!invoiceDetails || invoiceDetails.length === 0) {
                alert("No invoice data to export.");
                return;
            }
            const dataToExport = invoiceDetails.map((invoice) => {
                const formatted = {};
                for (const key in invoice) {
                    let value = invoice[key];
                    if (value && key.toLowerCase().includes("date")) {
                        const dateObj = new Date(value);
                        if (!isNaN(dateObj)) {
                            const day = String(dateObj.getDate()).padStart(2, '0');
                            const month = String(dateObj.getMonth() + 1).padStart(2, '0');
                            const year = dateObj.getFullYear();
                            value = `${day}-${month}-${year}`;
                        } else { value = "N/A"; }
                    }
                    if (value === null || value === undefined || value === "") value = "N/A";
                    const label = key.replace(/([a-z])([A-Z])/g, "$1 $2").replace(/_/g, " ").toUpperCase();
                    formatted[label] = value;
                }
                return formatted;
            });
            const worksheet = XLSX.utils.json_to_sheet(dataToExport);
            const cols = Object.keys(dataToExport[0]).map((key) => {
                const maxLength = dataToExport.reduce((acc, row) => {
                    const cellValue = row[key] || '';
                    return Math.max(acc, cellValue.toString().length);
                }, key.length);
                return { wch: maxLength + 2 };
            });
            worksheet['!cols'] = cols;
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, "Invoice Report");
            const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
            const blob = new Blob([excelBuffer], { type: "application/octet-stream" });
            saveAs(blob, "Invoice Report.xlsx");
        } catch (error) {
            console.error("Error exporting invoice data:", error);
            alert("Failed to export invoice data. Please try again.");
        }
    };

    return (
        <DashboardLayout>
            <div className="view-vendors-container">
                <div className="page-header">
                    <h2 className="page-title">
                        <span className="title-icon">🏢</span>
                        Invoice Overview
                    </h2>
                    <div className="title-underline"></div>
                </div>
                <div>
                    <div className="content-area">
                        <div className="filter-header">
                            <button
                                type="button"
                                className={`filter-toggle-btn ${showFilters ? "active" : ""}`}
                                onClick={() => setShowFilters(!showFilters)}
                            >
                                <span className="filter-icon">🔍</span>
                                <span className="filter-text">Filters</span>
                                <span className="filter-arrow">{showFilters ? "▲" : "▼"}</span>
                            </button>
                        </div>
                        {showFilters && (
                            <div className="filter-container">
                                <div className="filter-grid">
                                    <div className="filter-item">
                                        <label>Invoice Type</label>
                                        <select id="invoiceType" value={formData.invoiceType} onChange={handleInputChange}>
                                            <option value="0">Select</option>
                                            <option value="1">Tax Invoice</option>
                                            <option value="2">Debit Note</option>
                                            <option value="3">Credit Note</option>
                                            <option value="4">Proforma Invoice</option>
                                        </select>
                                    </div>
                                    <div className="filter-item">
                                        <label>Supplier Type</label>
                                        <select id="supplierType" value={formData.supplierType} onChange={handleInputChange}>
                                            <option value="0">Select</option>
                                            <option value="1">Supply of Material</option>
                                            <option value="2">Supply of Service</option>
                                            <option value="3">Both</option>
                                        </select>
                                    </div>
                                    <div className="filter-item">
                                        <label>Dept Head</label>
                                        <select value={deptId} onChange={(e) => setDeptId(e.target.value)}>
                                            <option value="">Select</option>
                                            {departmentHeads.map((head, index) => (
                                                <option key={`${head.empId}-${index}`} value={head.empId}>
                                                    {head.userName}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="filter-item">
                                        <label>Approved Status</label>
                                        <select id="approvedStatus" value={formData.approvedStatus} onChange={handleInputChange}>
                                            <option value="0">Select</option>
                                            {approvedStatusList.map((status) => (
                                                <option key={status.id} value={status.id}>{status.title}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="filter-item">
                                        <label>Invoice Number</label>
                                        <input
                                            type="text"
                                            id="invoiceNo"
                                            value={formData.invoiceNo}
                                            onChange={handleInputChange}
                                        />
                                    </div>
                                    <div className="filter-item">
                                        <label>Delivery Challan Number</label>
                                        <input
                                            type="text"
                                            id="deliveryChallanNumber"
                                            value={formData.deliveryChallanNumber}
                                            onChange={handleInputChange}
                                            autoComplete="off"
                                        />
                                    </div>
                                </div>
                                <div className="filter-actions">
                                    <button className="btn primary" onClick={handleSearch}>Search</button>
                                    <button className="btn success" onClick={handleExport}>Export</button>
                                    <button className="btn secondary" onClick={handleClear}>Clear</button>
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="table-controls vendor-toolbar">
                        <div className="vendor-toolbar-search">
                            <div className="vendor-search-box invoice-search-box">
                                <span className="vendor-search-icon" aria-hidden="true">
                                    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <circle cx="11" cy="11" r="8" />
                                        <line x1="21" y1="21" x2="16.65" y2="16.65" />
                                    </svg>
                                </span>
                                <Input
                                    type="text"
                                    placeholder="Search by Company Name..."
                                    value={searchTerm}
                                    onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                                    aria-label="Search by company name"
                                    className="vendor-search-input"
                                />
                                {searchTerm && (
                                    <button type="button" className="vendor-search-clear" aria-label="Clear search"
                                        onClick={() => { setSearchTerm(""); setCurrentPage(1); }}>
                                        <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <line x1="6" y1="6" x2="18" y2="18" />
                                            <line x1="18" y1="6" x2="6" y2="18" />
                                        </svg>
                                    </button>
                                )}
                            </div>
                        </div>
                        <div className="pagination-modern">
                            <button onClick={() => handlePageChange("first")} disabled={currentPage === 1}>⏮</button>
                            <button onClick={() => handlePageChange("prev")} disabled={currentPage === 1}>◀</button>
                            <span className="page-info">Page <strong>{currentPage}</strong> of <strong>{totalPages}</strong></span>
                            <button onClick={() => handlePageChange("next")} disabled={currentPage === totalPages}>▶</button>
                            <button onClick={() => handlePageChange("last")} disabled={currentPage === totalPages}>⏭</button>
                        </div>
                    </div>

                    <div className="content-area">
                        <div className="table-responsive">
                            <Table striped bordered hover className="invoices-table">
                                <thead>
                                    <tr>
                                        <th className="center-text">NAME OF COMPANY</th>
                                        <th className="center-text">INVOICE TYPE</th>
                                        <th className="center-text">SUPPLIER TYPE</th>
                                        <th className="center-text">INVOICE NO</th>
                                        <th className="center-text">INVOICE VALUE</th>
                                        <th className="center-text">TOTAL VALUE</th>
                                        <th className="center-text">CREATE DATE</th>
                                        <th className="center-text">VERIFIER NAME</th>
                                        <th className="center-text">DEPT HEAD</th>
                                        <th className="sortable" onClick={() => handleSort("approvalStatus")}>
                                            APPROVAL STATUS{" "}
                                            <span className="sort-icon">
                                                {sortBy === "approvalStatus" ? (sortOrder === "asc" ? "▲" : "▼") : "⇅"}
                                            </span>
                                        </th>
                                        <th colSpan={2} className="actions-header">ACTIONS</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {paginatedInvoices && paginatedInvoices.length > 0 ? (
                                        paginatedInvoices.map((invoice, index) => {
                                            const isPending = invoice.approvalStatusTitle
                                                ?.trim()
                                                .toLowerCase()
                                                .includes("pending");

                                            return (
                                                <tr key={invoice.ID || index}>
                                                <td>{invoice.nameOfCompany || "N/A"}</td>
                                                <td>{invoice.invoiceType || "N/A"}</td>
                                                <td>{invoice.invoiceSupplierTypeTitle || "N/A"}</td>
                                                <td>{invoice.invoiceNo || "N/A"}</td>
                                                <td>{invoice.invoiceValue != null ? invoice.invoiceValue.toFixed(2) : "0.00"}</td>
                                                <td>{invoice.totalValue != null ? invoice.totalValue.toFixed(2) : "0.00"}</td>
                                                <td>{invoice.createdDate ? new Date(invoice.createdDate).toLocaleDateString("en-GB").replace(/\//g, "-") : "N/A"}</td>
                                                <td>{invoice.verifierName || "N/A"}</td>
                                                <td>{invoice.deptHeadName || "N/A"}</td>
                                                <td>{invoice.approvalStatusTitle || "N/A"}</td>
                                                <td>
                                                    {/* ✅ Pass the full invoice object */}
                                                    <button className="action-btn view" onClick={() => handleView(invoice)}>
                                                        👁
                                                    </button>
                                                </td>
                                                <td>
                                                    {isPending && isFinanceTeam && (
                                                        <button className="action-btn reminder" onClick={() => handleReminder(invoice)}>
                                                            🔔
                                                        </button>
                                                    )}
                                                </td>
                                            </tr>
                                            );
                                        })
                                    ) : (
                                    <tr>
                                        <td colSpan="12" className="no-records">No records found.</td>
                                    </tr>
                                    )}
                                </tbody>
                            </Table>
                        </div>
                    </div>
                </div>
            </div>

            {/* ✅ Invoice Details Modal — mirrors Vendor Details modal */}
            <Modal isOpen={showModal} toggle={() => setShowModal(false)} centered className="modal-lg custom-modal">
                <ModalHeader toggle={() => setShowModal(false)}>Invoice Details</ModalHeader>
                {selectedInvoice && (
                    <div
                        style={{
                            padding: "12px 20px",
                            borderBottom: "1px solid #ddd",
                            background: "#f8f9fa"
                        }}
                    >
                        <div
                            style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "40px",
                                whiteSpace: "nowrap",
                                fontSize: "12px"
                            }}
                        >
                            <span>
                                <strong>Vendor Name :</strong> {selectedInvoice.nameOfCompany || "N/A"}
                            </span>

                            <span>
                                <strong>Vendor Code :</strong> {selectedInvoice.vendorCode || "N/A"}
                            </span>

                            <span>
                                <strong>Invoice Number :</strong> {selectedInvoice.invoiceNo || "N/A"}
                            </span>
                        </div>
                    </div>
                )}

                {/* Tabs */}
                
                <div className="tabs1">
                    <span className={activeTab === "vendor" ? "active-tab" : ""} onClick={() => setActiveTab("vendor")}>
                        Vendor Info
                    </span>
                    <span className={activeTab === "history" ? "active-tab" : ""} onClick={() => setActiveTab("history")}>
                        Invoice Approval History
                    </span>
                    <span className={activeTab === "info" ? "active-tab" : ""} onClick={() => setActiveTab("info")}>
                        Invoice Info
                    </span>
                </div>

                <ModalBody>
                    {/* Approval History Tab */}
                    {activeTab === "history" && (
                        <Table bordered className="history-table">
                            <thead>
                                <tr>
                                    <th>#</th>
                                    <th>Approval Status</th>
                                    <th>Approver Name</th>
                                    <th>Date</th>
                                    <th>Comments</th>
                                </tr>
                            </thead>
                            <tbody>
                                {approvalHistory.length > 0 ? (
                                    approvalHistory.map((item, index) => (
                                        <tr key={item.id || index}>
                                            <td>{index + 1}</td>
                                            <td>{item.approvalStatus || "N/A"}</td>
                                            <td>{item.approvalName || "N/A"}</td>
                                            <td>{item.createDate ? new Date(item.createDate).toLocaleString() : "N/A"}</td>
                                            <td>{item.comments || "N/A"}</td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="5" className="text-center">No history found</td>
                                    </tr>
                                )}
                            </tbody>
                        </Table>
                    )}
                    {/* Info Tab */}
                    {activeTab === "info" && invoiceInfo && (
                        <>
                            <Table bordered className="info-table1" style={{ fontSize: "12px" }}>
                            <tbody>
                                <tr>
                                    <th><strong>Invoice Type:</strong></th>
                                    <td>{invoiceInfo.invoiceType || "N/A"}</td>
                                    <th><strong>Supplier Type:</strong></th>
                                    <td>{invoiceInfo.invoiceSupplierType === 1 ? "Supply of Goods" : invoiceInfo.invoiceSupplierType === 2 ? "Supply of Service" : "N/A"}</td>
                                </tr>
                                <tr>
                                    <th><strong>Invoice Document:</strong></th>
                                    <td>{renderDocumentFile(invoiceInfo.invoiceFilePath, invoiceInfo.fileName, "Invoice Document Preview")}</td>
                                    <th><strong>Invoice Date:</strong></th>
                                    <td>{invoiceInfo.invoiceDate ? new Date(invoiceInfo.invoiceDate).toLocaleDateString("en-GB") : "N/A"}</td>
                                </tr>
                                <tr>
                                    <th><strong>Invoice No:</strong></th>
                                    <td>{invoiceInfo.invoiceNo || "N/A"}</td>
                                    <th><strong>PO No:</strong></th>
                                    <td>{invoiceInfo.pono || "N/A"}</td>
                                </tr>
                                <tr>
                                    <th><strong>Delivery Challan No:</strong></th>
                                    <td>{invoiceInfo.deliveryChallanNumber || "N/A"}</td>
                                    <th><strong>Invoice Value:</strong></th>
                                    <td>{invoiceInfo.invoiceValue?.toFixed(2) || "0.00"}</td>
                                </tr>
                                <tr>
                                    <th><strong>IGST:</strong></th>
                                    <td>{invoiceInfo.igst?.toFixed(2) || "0.00"}</td>
                                    <th><strong>CGST:</strong></th>
                                    <td>{invoiceInfo.cgst?.toFixed(2) || "0.00"}</td>
                                </tr>
                                <tr>
                                    <th><strong>SGST:</strong></th>
                                    <td>{invoiceInfo.sgst?.toFixed(2) || "0.00"}</td>
                                    <th><strong>Total Value:</strong></th>
                                    <td>{invoiceInfo.totalValue?.toFixed(2) || "0.00"}</td>
                                </tr>
                                <tr>
                                    <th><strong>Created Date:</strong></th>
                                    <td>{invoiceInfo.createdDate ? new Date(invoiceInfo.createdDate).toLocaleDateString("en-GB") : "N/A"}</td>
                                    <th><strong>Agreement Document:</strong></th>
                                    <td>{renderDocumentFile(invoiceInfo.agreementFilePath, invoiceInfo.fileNameAgreement, "Agreement Document Preview")}</td>
                                    </tr>
                                    <tr>
                                        <th><strong>Agreement Reference:</strong></th>
                                        <td colSpan={3}>
                                            {agreementRefs.length > 0 ? (
                                                <>
                                                    <div style={{ marginBottom: "4px" }}>
                                                        <strong>Agreement Date:</strong>{" "}
                                                        {agreementRefs[0].agreementDate
                                                            ? new Date(agreementRefs[0].agreementDate).toLocaleDateString("en-GB")
                                                            : "N/A"}
                                                    </div>
                                                    {renderDocumentFile(
                                                        agreementRefs[0].filePath,
                                                        agreementRefs[0].fileName,
                                                        "Agreement Reference Preview"
                                                    )}
                                                </>
                                            ) : (
                                                <span style={{ color: "gray" }}>No file uploaded</span>
                                            )}
                                        </td>
                                    </tr>
                                <tr>
                                    <th><strong>Email Approval:</strong></th>
                                    <td>{renderDocumentFile(invoiceInfo.poDocuments?.emailApprovalFilePath, "Email Approval", "Email Approval Preview")}</td>
                                    <th><strong>Invoice (PO Upload):</strong></th>
                                    <td>{renderDocumentFile(invoiceInfo.poDocuments?.invoiceIMAGEFilePath, "Invoice Document", "Invoice Preview")}</td>
                                </tr>
                                <tr>
                                    <th><strong>PO Attachment:</strong></th>
                                    <td>{renderDocumentFile(invoiceInfo.poDocuments?.poimageFilePath, "PO Document", "PO Preview")}</td>
                                    <th><strong>PR Attachment:</strong></th>
                                    <td>{renderDocumentFile(invoiceInfo.poDocuments?.primageFilePath, "PR Document", "PR Preview")}</td>
                                </tr>
                                <tr>
                                    <th><strong>Quotation:</strong></th>
                                    <td>{renderDocumentFile(invoiceInfo.poDocuments?.quotationIMAGEFilePath, "Quotation 1", "Quotation Preview")}</td>
                                    <th><strong>More Quotation:</strong></th>
                                    <td>{renderDocumentFile(invoiceInfo.poDocuments?.quotation2IMAGEFilePath, "Quotation 2", "Quotation Preview")}</td>
                                </tr>
                                <tr>
                                    <th><strong>Comparison Quotes:</strong></th>
                                    <td>{renderDocumentFile(invoiceInfo.poDocuments?.comparisonquotesFilePath, "Comparison Quotes", "Comparison Preview")}</td>
                                        <th><strong>Evaluation Form:</strong></th>
                                        <td>{invoiceInfo.evaluationForm ? <a href={invoiceInfo.evaluationForm} target="_blank" rel="noopener noreferrer">View</a> : "N/A"}</td>
                                    </tr>
                                    <tr>
                                        <th><strong>Other Documents:</strong></th>
                                    <td>{renderDocumentFile(invoiceInfo.poDocuments?.otherDocuementsIMAGEFilePath, "Other Documents", "Other Documents Preview")}</td>
                                        <th><strong>TimeSheet:</strong></th>
                                        <td>
                                            {invoiceInfo.timeSheetFilePath ? (
                                                <div style={{ display: "flex", flexDirection: "column" }}>
                                                    <span style={{ color: "#0a4275", fontWeight: "500" }}>
                                                        {invoiceInfo.fileNameTimeSheet}
                                                    </span>
                                                    <div style={{ display: "flex", gap: "8px", marginTop: "5px" }}>
                                                        {isMsgTimesheet() && (
                                                            <Button color="primary" size="sm" outline onClick={viewTimesheetMessage}>
                                                                View Message
                                                            </Button>
                                                        )}
                                                        {!isMsgTimesheet() && isViewableTimesheet() && (
                                                            <Button color="primary" size="sm" outline onClick={viewTimesheet}>
                                                                View
                                                            </Button>
                                                        )}
                                                        {isExcelTimesheet() && (
                                                            <Button color="success" size="sm" outline onClick={downloadTimesheet}>
                                                                Download
                                                            </Button>
                                                        )}
                                                    </div>
                                                </div>
                                            ) : (
                                                <span style={{ color: "gray" }}>No file uploaded</span>
                                            )}
                                        </td>
                                </tr>
                                        <tr>
                                            <th><strong>GRN Attachment:</strong></th>
                                            <td>{invoiceInfo.grnAttachment ? <a href={invoiceInfo.grnAttachment} target="_blank" rel="noopener noreferrer">View</a> : "N/A"}</td>
                                            <th><strong>Quotation 3:</strong></th>
                                            <td>{renderDocumentFile(invoiceInfo.poDocuments?.quotation3IMAGEFilePath, "Quotation 3", "Quotation 3 Preview")}</td>
                                        </tr>
                                </tbody>
                            </Table>
                            {/* Goods Received Table */}
                            <h5 className="text-primary fw-bold mb-3" style={{ marginTop: "20px" }}>
                                The Following Goods has been Received.
                            </h5>
                            <table className="table table-bordered align-middle table-hover invoice-table" style={{ fontSize: "12px" }}>
                                <thead className="table-header11 text-center">
                                    <tr>
                                        <th>Sr. No</th>
                                        <th>Description</th>
                                        <th>Ordered Qty</th>
                                        <th>Supplied Qty</th>
                                        <th>Invoice Value</th>
                                        <th>CGST (%)</th>
                                        <th>SGST (%)</th>
                                        <th>IGST (%)</th>
                                        <th>Total Value</th>
                                        <th>Remarks</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {invoiceInfo?.grnItems?.length > 0 ? (
                                        invoiceInfo.grnItems.map((item, index) => (
                                            <tr key={item.id || index} className="text-center">
                                                <td>{index + 1}</td>
                                                <td className="text-start">{item.description || "-"}</td>
                                                <td>{item.qty ?? 0}</td>
                                                <td>{item.rcvQty ?? 0}</td>
                                                <td>{item.invoiceValue?.toFixed(2) ?? "0.00"}</td>
                                                <td>{item.cgst ?? 0}</td>
                                                <td>{item.sgst ?? 0}</td>
                                                <td>{item.igst ?? 0}</td>
                                                <td>{item.totalValue?.toFixed(2) ?? "0.00"}</td>
                                                <td>{item.remark || "-"}</td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="10" style={{ textAlign: "center" }}>No GRN Items Available</td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </>
                    )}

                    {activeTab === "vendor" && selectedVendor && (
                        <VendorInfoTabs
                            selectedVendor={selectedVendor}
                            vendorFiles={vendorFiles}
                            isEditing={isEditing}
                            setIsEditing={setIsEditing}
                            handleInputChange={(e, field) =>
                                setSelectedVendor({ ...selectedVendor, [field]: e.target.value })
                            }
                            subTab={subTab}
                            setSubTab={setSubTab}
                            type="invoice"
                        />
                    )}
                </ModalBody>

                <ModalFooter>
                    <Button color="secondary" onClick={() => setShowModal(false)}>Close</Button>
                </ModalFooter>
            </Modal>
            <Modal isOpen={showPreview} toggle={() => setShowPreview(false)} size="lg" centered style={{ zIndex: 1060 }}>
                <ModalHeader toggle={() => setShowPreview(false)}>{previewTitle}</ModalHeader>
                <ModalBody style={{ padding: 0 }}>
                    {previewUrl ? (
                        <iframe src={previewUrl} title="Preview" style={{ width: "100%", height: "80vh", border: "none" }} />
                    ) : (
                        <p style={{ padding: "20px" }}>No preview available</p>
                    )}
                </ModalBody>
            </Modal>
            {/* ✅ Reminder Toast */}
            {showReminderToast && reminderInvoice && (
                <div style={{
                    position: "fixed",
                    bottom: "30px",
                    right: "30px",
                    zIndex: 9999,
                    background: "#1f4e78",
                    color: "#fff",
                    borderRadius: "12px",
                    padding: "16px 20px",
                    boxShadow: "0 8px 24px rgba(0,0,0,0.25)",
                    minWidth: "320px",
                    maxWidth: "400px",
                    animation: "slideIn 0.3s ease"
                }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                        <div style={{ display: "flex", gap: "12px", alignItems: "flex-start" }}>
                            <span style={{ fontSize: "24px" }}>🔔</span>
                            <div>
                                <div style={{ fontWeight: "700", fontSize: "15px", marginBottom: "8px" }}>
                                    Pending Approval Reminder
                                </div>
                                <div style={{ fontSize: "12px", opacity: 0.95, lineHeight: "1.8" }}>
                                    <div>📄 Invoice No: <strong>{reminderInvoice.invoiceNo || "N/A"}</strong></div>
                                    <div>🏢 Company: <strong>{reminderInvoice.nameOfCompany || "N/A"}</strong></div>
                                    <div>💰 Invoice Value: <strong>{reminderInvoice.invoiceValue != null ? reminderInvoice.invoiceValue.toFixed(2) : "0.00"}</strong></div>
                                    <div>👤 Verifier: <strong>{reminderInvoice.verifierName || "N/A"}</strong></div>
                                    <div>👥 Dept Head: <strong>{reminderInvoice.deptHeadName || "N/A"}</strong></div>
                                    <div>📊 Current Status: <strong>{reminderInvoice.approvalStatusTitle || "N/A"}</strong></div>
                                    <div>📧 Sent To: <strong>{reminderInvoice.testEmail || "N/A"}</strong></div>
                                    <div style={{
                                        marginTop: "8px",
                                        padding: "6px 10px",
                                        background: "rgba(255,255,255,0.15)",
                                        borderRadius: "6px",
                                        fontSize: "11px"
                                    }}>
                                        ⚠️ This invoice is pending approval. Please review and take action.
                                    </div>
                                </div>
                            </div>
                        </div>
                        <button
                            onClick={() => setShowReminderToast(false)}
                            style={{
                                background: "none", border: "none", color: "#fff",
                                cursor: "pointer", fontSize: "18px", padding: "0",
                                marginLeft: "10px", opacity: 0.8, lineHeight: 1
                            }}
                        >✕</button>
                    </div>
                    {/* Progress bar */}
                    <div style={{ marginTop: "12px", background: "rgba(255,255,255,0.2)", borderRadius: "4px", height: "3px" }}>
                        <div style={{
                            height: "3px", background: "#fff", borderRadius: "4px",
                            animation: "shrink 5s linear forwards"
                        }} />
                    </div>
                </div>
            )}

            <style>{`
    @keyframes slideIn {
        from { transform: translateX(120px); opacity: 0; }
        to   { transform: translateX(0);     opacity: 1; }
    }
    @keyframes shrink {
        from { width: 100%; }
        to   { width: 0%;   }
    }
    .info-table1, .info-table1 th, .info-table1 td, .info-table1 strong,
    .invoice-table, .invoice-table th, .invoice-table td {
        font-size: 12px !important;
    }
`}</style>

        </DashboardLayout>
    );
};

export default ViewAllInvoiceDetails;